import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  canonicalRequest,
  EditorPageShell,
  EmptyState,
  RemoteEntityAutocomplete,
  SectionCard,
  useAddressableTabs,
} from './CanonicalEditorCommon';
import { CreatePublicationDialog, ManagePublicationDialog } from './CanonicalPublicationDialogs';
import CanonicalPricingPolicyDialog from './CanonicalPricingPolicyDialog';
import { EntityStatGrid, PublicationCard, ReadinessPanel } from '../../components/workspace/EntityPanels';
import ResourceActivityPanel from '../../components/workspace/ResourceActivityPanel';
import { formatDate, labelize, publicationHealth, StatusChip } from '../../components/workspace/WorkspaceStatus';

const listingStatuses = ['draft', 'ready', 'archived'];
const tabs = ['Content', 'Media', 'Pricing', 'Channels', 'Publication Status', 'Activity'];

function temporaryKey(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function emptyForm() {
  return {
    product: null,
    title: '',
    description: '',
    tagsText: '',
    language: 'en',
    currency: 'USD',
    status: 'draft',
    variants: [],
    media: [],
    removedVariantIDs: [],
    removedMediaIDs: [],
  };
}

function formFromListing(listing) {
  return {
    product: listing.product || null,
    title: listing.title || '',
    description: listing.description || '',
    tagsText: (listing.tags || []).join(', '),
    language: listing.language || 'en',
    currency: listing.currency || 'USD',
    status: listing.status || 'draft',
    variants: (listing.variants || []).map((variant) => ({
      ...variant,
      _key: variant.id,
      priceText: (Number(variant.price || 0) / 100).toFixed(2),
    })),
    media: (listing.media || []).map((item) => ({ ...item, _key: item.id })),
    removedVariantIDs: [],
    removedMediaIDs: [],
  };
}

function tagsFromText(value) {
  const seen = new Set();
  return String(value || '')
    .split(/[\n,]+/)
    .map((tag) => tag.trim().replace(/\s+/g, ' '))
    .filter((tag) => {
      const key = tag.toLowerCase();
      if (!tag || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function centsFromText(value, label) {
  const amount = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error(`${label} must be a valid non-negative amount.`);
  }
  return Math.round(amount * 100);
}

function variantKey(variant, index) {
  return variant._key || variant.id || `variant-${index}`;
}

function formatMoney(cents, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100);
  } catch (error) {
    return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
  }
}

function formatBasisPoints(value) {
  return `${(Number(value || 0) / 100).toFixed(2).replace(/\.00$/, '')}%`;
}

function listingPayload(form, expectedVersion) {
  const payload = {
    title: form.title.trim(),
    description: form.description.trim(),
    tags: tagsFromText(form.tagsText),
    language: form.language.trim().toLowerCase(),
    currency: form.currency.trim().toUpperCase(),
    status: form.status,
    variants: [
      ...form.variants.map((variant, index) => ({
        ...(variant.id ? { id: variant.id } : {}),
        product_variant_id: variant.product_variant_id || null,
        sku: String(variant.sku || '').trim(),
        price: centsFromText(variant.priceText, `Variant ${index + 1} price`),
        currency: form.currency.trim().toUpperCase(),
        enabled: variant.enabled !== false,
      })),
      ...form.removedVariantIDs.map((id) => ({ id, remove: true })),
    ],
    media: [
      ...form.media.map((item, index) => ({
        ...(item.id ? { id: item.id } : {}),
        image_id: item.image_id || null,
        source_url: String(item.source_url || '').trim(),
        media_type: item.media_type || 'image',
        view_key: item.view_key || '',
        position: index,
        featured: item.featured === true,
        alt_text: String(item.alt_text || '').trim(),
      })),
      ...form.removedMediaIDs.map((id) => ({ id, remove: true })),
    ],
  };
  if (expectedVersion) {
    payload.expected_version = expectedVersion;
  } else {
    payload.product_id = form.product?.id;
  }
  return payload;
}

function seedVariants(product) {
  return (product?.variants || []).map((variant) => ({
    _key: temporaryKey('variant'),
    product_variant_id: variant.id,
    sku: variant.sku || '',
    priceText: '0.00',
    enabled: variant.enabled !== false,
  }));
}

const CanonicalListingEditor = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const requestedProductID = searchParams.get('product_id') || '';
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const isNew = !id || id === 'new';
  const [listing, setListing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [productDetail, setProductDetail] = React.useState(null);
  const [tab, setTab] = useAddressableTabs(tabs);
  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [validation, setValidation] = React.useState(null);
  const [readiness, setReadiness] = React.useState(null);
  const [createPublicationOpen, setCreatePublicationOpen] = React.useState(false);
  const [managedPublication, setManagedPublication] = React.useState(null);
  const [pricingPolicies, setPricingPolicies] = React.useState([]);
  const [selectedPolicyID, setSelectedPolicyID] = React.useState('');
  const [shippingOverrideText, setShippingOverrideText] = React.useState('');
  const [priceCalculations, setPriceCalculations] = React.useState({});
  const [pricingNotice, setPricingNotice] = React.useState('');
  const [calculating, setCalculating] = React.useState(false);
  const [pricingPolicyOpen, setPricingPolicyOpen] = React.useState(false);
  const [editingPricingPolicy, setEditingPricingPolicy] = React.useState(null);

  const loadPricingPolicies = React.useCallback(async (preferredID = '') => {
    const payload = await canonicalRequest(fetchWithAuth, '/pricing-policies?limit=200');
    const items = payload.items || [];
    setPricingPolicies(items);
    setSelectedPolicyID((current) => {
      const candidate = preferredID || current;
      if (candidate && items.some((item) => item.id === candidate)) return candidate;
      return items.find((item) => item.active !== false)?.id || items[0]?.id || '';
    });
    return items;
  }, [fetchWithAuth]);

  const loadProduct = React.useCallback(async (productID, preserveVariants = true) => {
    if (!productID) {
      setProductDetail(null);
      return null;
    }
    const payload = await canonicalRequest(fetchWithAuth, `/products/${productID}`);
    setProductDetail(payload.item);
    setForm((current) => ({
      ...current,
      product: payload.item,
      variants: preserveVariants && current.variants.length ? current.variants : seedVariants(payload.item),
    }));
    return payload.item;
  }, [fetchWithAuth]);

  const loadListing = React.useCallback(async () => {
    if (isNew) {
      setListing(null);
      setForm(emptyForm());
      if (requestedProductID) {
        try {
          await loadProduct(requestedProductID, false);
        } catch (loadError) {
          setError(loadError.message || 'Unable to load selected product');
        }
      }
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await canonicalRequest(fetchWithAuth, `/listings/${id}`);
      setListing(payload.item);
      setForm(formFromListing(payload.item));
      setValidation(null);
      canonicalRequest(fetchWithAuth, `/listings/${id}/validate`, { method: 'POST' })
        .then(setReadiness)
        .catch(() => setReadiness(null));
      await loadProduct(payload.item.product.id, true);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load listing');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, id, isNew, loadProduct, requestedProductID]);

  React.useEffect(() => {
    loadListing();
  }, [loadListing]);

  React.useEffect(() => {
    loadPricingPolicies().catch((loadError) => setPricingNotice(loadError.message || 'Unable to load pricing policies'));
  }, [loadPricingPolicies]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    setValidation(null);
    try {
      if (isNew && !form.product?.id) {
        throw new Error('Choose a product before saving this listing.');
      }
      const payload = listingPayload(form, listing?.version);
      const result = await canonicalRequest(fetchWithAuth, isNew ? '/listings' : `/listings/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        body: JSON.stringify(payload),
      });
      setValidation(result.validation || null);
      setReadiness(result.validation || null);
      if (isNew) {
        navigate(`/listings/${result.id}`, { replace: true });
      } else {
        await loadListing();
      }
    } catch (saveError) {
      setValidation(saveError.validation || null);
      setError(saveError.status === 409
        ? 'This listing changed since you opened it. Reload before applying your edits.'
        : saveError.message || 'Unable to save listing');
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    if (isNew) {
      setError('Save the draft once before running a server readiness check.');
      return;
    }
    setError('');
    try {
      const result = await canonicalRequest(fetchWithAuth, `/listings/${id}/validate`, { method: 'POST' });
      setValidation(result);
      setReadiness(result);
    } catch (validateError) {
      setValidation(validateError.validation || null);
      setError(validateError.message || 'Unable to validate listing');
    }
  };

  const selectProduct = async (product) => {
    setError('');
    if (!product) {
      setProductDetail(null);
      setForm((current) => ({ ...current, product: null, variants: [] }));
      return;
    }
    try {
      await loadProduct(product.id, false);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load product variants');
    }
  };

  const updateVariant = (index, patch) => setForm((current) => ({
    ...current,
    variants: current.variants.map((variant, itemIndex) => itemIndex === index ? { ...variant, ...patch } : variant),
  }));

  const addVariant = () => setForm((current) => ({
    ...current,
    variants: [...current.variants, { _key: temporaryKey('variant'), product_variant_id: null, sku: '', priceText: '0.00', enabled: true }],
  }));

  const selectedPricingPolicy = pricingPolicies.find((policy) => policy.id === selectedPolicyID) || null;

  const calculatePrices = async () => {
    if (!selectedPricingPolicy) {
      setPricingNotice('Choose or create a pricing policy first.');
      return;
    }
    let shippingCents = null;
    try {
      shippingCents = shippingOverrideText.trim() === ''
        ? null
        : centsFromText(shippingOverrideText, 'Shipping override');
    } catch (inputError) {
      setPricingNotice(inputError.message);
      return;
    }
    const missing = [];
    const items = [];
    form.variants.forEach((variant, index) => {
      const productVariant = (productDetail?.variants || []).find((item) => item.id === variant.product_variant_id);
      const offerVariant = productVariant?.fulfillment_offer_variant;
      if (!offerVariant || !Number.isFinite(Number(offerVariant.cost))) {
        missing.push(productVariant?.title || variant.sku || `Variant ${index + 1}`);
        return;
      }
      if ((offerVariant.currency || form.currency).toUpperCase() !== form.currency.toUpperCase()) {
        missing.push(`${productVariant?.title || variant.sku || `Variant ${index + 1}`} (currency mismatch)`);
        return;
      }
      items.push({
        key: variantKey(variant, index),
        base_cost_cents: Number(offerVariant.cost),
        ...(shippingCents === null ? {} : { shipping_cents: shippingCents }),
      });
    });
    if (!items.length) {
      setPricingNotice('No listing variant has an exact fulfillment-cost mapping yet. Re-import offer variants or rerun the canonical product backfill.');
      return;
    }
    setCalculating(true);
    setPricingNotice('');
    try {
      const result = await canonicalRequest(fetchWithAuth, '/pricing/calculate', {
        method: 'POST',
        body: JSON.stringify({ policy_id: selectedPricingPolicy.id, currency: form.currency, items }),
      });
      const next = {};
      (result.calculations || []).forEach((item) => { next[item.key] = item.breakdown; });
      setPriceCalculations(next);
      if (missing.length) {
        setPricingNotice(`Calculated ${items.length} variant${items.length === 1 ? '' : 's'}. Missing exact costs for: ${missing.join(', ')}.`);
      }
    } catch (calculationError) {
      setPricingNotice(calculationError.message || 'Unable to calculate suggested prices');
    } finally {
      setCalculating(false);
    }
  };

  const applySuggestedPrices = () => {
    setForm((current) => ({
      ...current,
      variants: current.variants.map((variant, index) => {
        const calculation = priceCalculations[variantKey(variant, index)];
        return calculation
          ? { ...variant, priceText: (Number(calculation.suggested_price_cents) / 100).toFixed(2) }
          : variant;
      }),
    }));
    setPricingNotice('Suggestions were copied into this local draft. Save the Listing when you are ready.');
  };

  const pricingPolicySaved = async (policy) => {
    setPricingPolicyOpen(false);
    setEditingPricingPolicy(null);
    await loadPricingPolicies(policy.id);
  };

  const removeVariant = (index) => setForm((current) => {
    const target = current.variants[index];
    return {
      ...current,
      variants: current.variants.filter((item, itemIndex) => itemIndex !== index),
      removedVariantIDs: target?.id ? [...current.removedVariantIDs, target.id] : current.removedVariantIDs,
    };
  });

  const updateMedia = (index, patch) => setForm((current) => ({
    ...current,
    media: current.media.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));

  const addMedia = () => setForm((current) => ({
    ...current,
    media: [...current.media, {
      _key: temporaryKey('media'), image_id: null, source_url: '', media_type: 'image', featured: current.media.length === 0, alt_text: '',
    }],
  }));

  const removeMedia = (index) => setForm((current) => {
    const target = current.media[index];
    const next = current.media.filter((item, itemIndex) => itemIndex !== index);
    if (target?.featured && next.length) next[0] = { ...next[0], featured: true };
    return {
      ...current,
      media: next,
      removedMediaIDs: target?.id ? [...current.removedMediaIDs, target.id] : current.removedMediaIDs,
    };
  });

  const moveMedia = (index, direction) => setForm((current) => {
    const target = index + direction;
    if (target < 0 || target >= current.media.length) return current;
    const next = [...current.media];
    [next[index], next[target]] = [next[target], next[index]];
    return { ...current, media: next };
  });

  const selectFeatured = (index) => setForm((current) => ({
    ...current,
    media: current.media.map((item, itemIndex) => ({ ...item, featured: itemIndex === index })),
  }));

  const publications = listing?.publications || [];
  const readinessChecks = [
    { key: 'product', label: 'Product selected', complete: Boolean(form.product?.id), help: form.product?.name || 'Choose a reusable product.' },
    { key: 'content', label: 'Content prepared', complete: Boolean(form.title.trim() && form.description.trim()), help: 'Title and description are present.' },
    { key: 'variants', label: 'Sellable variants priced', complete: form.variants.some((variant) => variant.enabled !== false && Number.parseFloat(variant.priceText) > 0), help: `${form.variants.filter((variant) => variant.enabled !== false).length} enabled.` },
    { key: 'media', label: 'Primary media selected', complete: form.media.some((item) => item.featured && (item.source_url || item.image_id)), help: `${form.media.length} media item${form.media.length === 1 ? '' : 's'}.` },
    { key: 'channel', label: 'Sales channel configured', complete: publications.length > 0, help: `${publications.length} destination${publications.length === 1 ? '' : 's'}.` },
    { key: 'verified', label: 'Remote state verified', complete: publications.length > 0 && publications.every((item) => publicationHealth(item) === 'verified'), help: `${publications.filter((item) => publicationHealth(item) === 'verified').length} verified.` },
  ];

  return (
    <EditorPageShell
      backTo="/listings"
      eyebrow={isNew ? 'Create listing' : 'Listing'}
      title={form.title || (isNew ? 'Untitled listing' : 'Listing')}
      subtitle={isNew ? 'Build reusable merchandising intent before choosing a sales channel.' : listing?.id}
      status={form.status}
      version={listing?.version}
      loading={loading}
      saving={saving}
      error={error}
      validation={validation}
      onReload={isNew ? null : loadListing}
      onValidate={validate}
      onSave={save}
      extraActions={!isNew && form.product?.id ? <Button variant="outlined" onClick={() => navigate(`/products/${form.product.id}`)}>View product</Button> : null}
    >
      <Paper variant="outlined">
        <Tabs value={tab} onChange={(event, next) => setTab(next)} variant="scrollable" scrollButtons="auto" aria-label="Listing editor sections">
          {tabs.map((label, index) => <Tab key={label} id={`listing-editor-tab-${index}`} aria-controls={`listing-editor-panel-${index}`} label={label} />)}
        </Tabs>
      </Paper>

      <Box role="tabpanel" id={`listing-editor-panel-${tab}`} aria-labelledby={`listing-editor-tab-${tab}`} tabIndex={0}>
      {tab === 0 ? (
        <Stack spacing={2}>
          {!isNew ? (
            <>
              <EntityStatGrid items={[
                { label: 'Variants', value: form.variants.length, helper: `${form.variants.filter((variant) => variant.enabled !== false).length} enabled` },
                { label: 'Media', value: form.media.length, helper: form.media.some((item) => item.featured) ? 'Primary media selected' : 'No primary media' },
                { label: 'Channels', value: publications.length, helper: publications.map((item) => labelize(item.provider)).join(', ') || 'Not published yet' },
                { label: 'Verified', value: publications.filter((item) => publicationHealth(item) === 'verified').length, helper: 'Remote destinations in sync' },
              ]} />
              <ReadinessPanel
                validation={readiness}
                checks={readinessChecks}
                title="Listing readiness"
                description="A reusable Listing can be complete before any channel is changed. Each destination is then planned and verified independently."
              />
            </>
          ) : null}
          <SectionCard title="Listing identity" description="This content is reusable across channels. Per-channel differences belong in Publication overrides.">
            <Stack spacing={2}>
              <RemoteEntityAutocomplete
                fetchWithAuth={fetchWithAuth}
                endpoint="/products"
                label="Product"
                value={form.product}
                onChange={selectProduct}
                getOptionLabel={(option) => option.name}
                helperText={isNew ? 'Choose the physical product this listing sells.' : 'A listing remains attached to its original product.'}
                disabled={!isNew}
                required
              />
              <TextField label="Listing title" required value={form.title} onChange={(event) => setField('title', event.target.value)} />
              <TextField label="Description" multiline minRows={8} value={form.description} onChange={(event) => setField('description', event.target.value)} />
              <TextField label="Tags" multiline minRows={2} value={form.tagsText} onChange={(event) => setField('tagsText', event.target.value)} helperText="Separate tags with commas or new lines. Duplicate tags are removed when saved." />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <TextField label="Language" value={form.language} onChange={(event) => setField('language', event.target.value)} />
                <TextField label="Currency" value={form.currency} onChange={(event) => setField('currency', event.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} />
                <TextField select label="Lifecycle status" value={form.status} onChange={(event) => setField('status', event.target.value)}>
                  {listingStatuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </TextField>
              </Box>
            </Stack>
          </SectionCard>
        </Stack>
      ) : null}

      {tab === 1 ? (
        <SectionCard title="Listing media" description="Order is canonical and keyboard-accessible. Primary media is separate from the approved custom Featured mockup tracked on the Product." action={<Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addMedia}>Add media</Button>}>
          {form.media.length === 0 ? (
            <EmptyState title="No mockups yet" description="Add a durable URL or reference an uploaded image by UUID." />
          ) : (
            <Stack spacing={1.5}>
              {form.media.map((item, index) => (
                <Paper key={item._key || item.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <Box sx={{ width: 92, height: 92, flexShrink: 0, borderRadius: 1.5, bgcolor: 'background.default', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                      {item.source_url ? <Box component="img" src={item.source_url} alt={item.alt_text || `Listing media ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Typography variant="caption" color="text.secondary">Preview</Typography>}
                    </Box>
                    <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.5 }}>
                        <TextField label="Media URL" value={item.source_url || ''} onChange={(event) => updateMedia(index, { source_url: event.target.value })} />
                        <TextField label="Image UUID (optional)" value={item.image_id || ''} onChange={(event) => updateMedia(index, { image_id: event.target.value || null })} />
                      </Box>
                      <TextField label="Alt text" value={item.alt_text || ''} onChange={(event) => updateMedia(index, { alt_text: event.target.value })} />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Primary media"><Radio checked={item.featured === true} onChange={() => selectFeatured(index)} inputProps={{ 'aria-label': `Make media ${index + 1} primary` }} /></Tooltip>
                      <Tooltip title="Move earlier"><span><IconButton onClick={() => moveMedia(index, -1)} disabled={index === 0} aria-label={`Move media ${index + 1} earlier`}><ArrowUpwardIcon /></IconButton></span></Tooltip>
                      <Tooltip title="Move later"><span><IconButton onClick={() => moveMedia(index, 1)} disabled={index === form.media.length - 1} aria-label={`Move media ${index + 1} later`}><ArrowDownwardIcon /></IconButton></span></Tooltip>
                      <Tooltip title="Remove"><IconButton onClick={() => removeMedia(index)} aria-label={`Remove media ${index + 1}`}><DeleteOutlineIcon /></IconButton></Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 2 ? (
        <SectionCard
          title="Variant pricing"
          description={`Prices are entered in ${form.currency || 'the listing currency'} and stored as integer cents. Policy suggestions stay local until you apply them and save this draft.`}
          action={(
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {Object.keys(priceCalculations).length ? <Button size="small" variant="outlined" onClick={applySuggestedPrices}>Apply suggestions</Button> : null}
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addVariant}>Add variant</Button>
            </Stack>
          )}
        >
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(180px, .7fr) auto' }, gap: 1.5, alignItems: 'start' }}>
                <TextField select label="Pricing policy" value={selectedPolicyID} onChange={(event) => { setSelectedPolicyID(event.target.value); setPriceCalculations({}); }}>
                  <MenuItem value=""><em>No policy selected</em></MenuItem>
                  {pricingPolicies.map((policy) => <MenuItem key={policy.id} value={policy.id}>{policy.name}{policy.active === false ? ' (inactive)' : ''}</MenuItem>)}
                </TextField>
                <TextField
                  label={`Shipping override (${form.currency})`}
                  value={shippingOverrideText}
                  onChange={(event) => { setShippingOverrideText(event.target.value); setPriceCalculations({}); }}
                  inputMode="decimal"
                  helperText="Blank uses the policy default."
                />
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="outlined" onClick={() => { setEditingPricingPolicy(null); setPricingPolicyOpen(true); }}>New policy</Button>
                  <Button variant="outlined" disabled={!selectedPricingPolicy} onClick={() => { setEditingPricingPolicy(selectedPricingPolicy); setPricingPolicyOpen(true); }}>Edit</Button>
                  <Button variant="contained" disabled={!selectedPricingPolicy || calculating || form.variants.length === 0} onClick={calculatePrices}>
                    {calculating ? 'Calculating…' : 'Calculate'}
                  </Button>
                </Stack>
              </Box>
              {selectedPricingPolicy ? (
                <Typography variant="caption" color="text.secondary">
                  {formatBasisPoints(selectedPricingPolicy.target_margin_bps)} target margin · {formatBasisPoints(selectedPricingPolicy.percentage_fee_bps)} percentage fees · {formatMoney(selectedPricingPolicy.fixed_fee_cents, selectedPricingPolicy.currency)} fixed fee · prices round up to .{String(selectedPricingPolicy.ending_cents).padStart(2, '0')}
                </Typography>
              ) : null}
              {pricingNotice ? <Alert severity={Object.keys(priceCalculations).length ? 'info' : 'warning'}>{pricingNotice}</Alert> : null}
            </Stack>
          </Paper>
          {form.variants.length === 0 ? (
            <EmptyState title="No listing variants" description="Choose a Product to seed its variants, or add one manually." />
          ) : (
            <Stack spacing={1.5}>
              {form.variants.map((variant, index) => {
                const productVariant = (productDetail?.variants || []).find((item) => item.id === variant.product_variant_id);
                const offerVariant = productVariant?.fulfillment_offer_variant;
                const calculation = priceCalculations[variantKey(variant, index)];
                return (
                  <Paper key={variantKey(variant, index)} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr auto auto' }, gap: 1.5, alignItems: 'center' }}>
                        <TextField
                          select
                          label="Product variant"
                          value={variant.product_variant_id || ''}
                          onChange={(event) => {
                            const selectedVariant = (productDetail?.variants || []).find((item) => item.id === event.target.value);
                            updateVariant(index, { product_variant_id: event.target.value || null, sku: selectedVariant?.sku || variant.sku });
                            setPriceCalculations({});
                          }}
                        >
                          <MenuItem value=""><em>Unmapped</em></MenuItem>
                          {(productDetail?.variants || []).map((item) => <MenuItem key={item.id} value={item.id}>{item.title || item.sku}</MenuItem>)}
                        </TextField>
                        <TextField label="SKU" value={variant.sku || ''} onChange={(event) => updateVariant(index, { sku: event.target.value })} />
                        <TextField label={`Price (${form.currency})`} value={variant.priceText || ''} onChange={(event) => updateVariant(index, { priceText: event.target.value })} inputMode="decimal" />
                        <FormControlLabel control={<Checkbox checked={variant.enabled !== false} onChange={(event) => updateVariant(index, { enabled: event.target.checked })} />} label="Enabled" />
                        <Tooltip title="Remove variant"><IconButton onClick={() => removeVariant(index)} aria-label={`Remove price variant ${index + 1}`}><DeleteOutlineIcon /></IconButton></Tooltip>
                      </Box>
                      <Typography variant="caption" color={offerVariant ? 'text.secondary' : 'warning.main'}>
                        {offerVariant
                          ? `Fulfillment cost: ${formatMoney(offerVariant.cost, offerVariant.currency)} · ${offerVariant.title}`
                          : 'Exact fulfillment cost is unavailable for this mapped product variant.'}
                      </Typography>
                      {calculation ? (
                        <>
                          <Divider />
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 3 }} alignItems={{ md: 'center' }} justifyContent="space-between">
                            <Stack direction="row" spacing={2.5} useFlexGap flexWrap="wrap">
                              <Box><Typography variant="caption" color="text.secondary">Suggested</Typography><Typography variant="subtitle2">{formatMoney(calculation.suggested_price_cents, calculation.currency)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Shipping</Typography><Typography variant="body2">{formatMoney(calculation.shipping_cents, calculation.currency)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Fees</Typography><Typography variant="body2">{formatMoney(calculation.fixed_fee_cents + calculation.percentage_fee_cents, calculation.currency)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Estimated profit</Typography><Typography variant="body2">{formatMoney(calculation.estimated_profit_cents, calculation.currency)} ({formatBasisPoints(calculation.effective_margin_bps)})</Typography></Box>
                            </Stack>
                            <Button size="small" variant="outlined" onClick={() => updateVariant(index, { priceText: (Number(calculation.suggested_price_cents) / 100).toFixed(2) })}>Use suggestion</Button>
                          </Stack>
                        </>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 3 ? (
        <SectionCard
          title="Sales channels"
          description="Each Publication independently projects this Listing into a destination and declares which fields PODBoss owns."
          action={!isNew ? <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreatePublicationOpen(true)}>Add channel</Button> : null}
        >
          {isNew ? <Alert severity="info">Save this Listing draft before adding a channel.</Alert> : null}
          {!isNew && publications.length === 0 ? (
            <EmptyState title="Not published anywhere" description="Add a sales channel without triggering a remote write." action={<Button variant="contained" onClick={() => setCreatePublicationOpen(true)}>Add channel</Button>} />
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {publications.map((publication) => (
                <PublicationCard key={publication.id} publication={publication} onManage={setManagedPublication} />
              ))}
            </Box>
          )}
        </SectionCard>
      ) : null}

      {tab === 4 ? (
        <SectionCard title="Publication status" description="Success will eventually mean verified remote state, never merely a completed background job.">
          {publications.length === 0 ? (
            <EmptyState title="No publication state" description="Add a channel first, then preview its immutable publication plan." />
          ) : (
            <Stack spacing={1.5}>
              <EntityStatGrid items={[
                { label: 'Destinations', value: publications.length },
                { label: 'Verified', value: publications.filter((item) => publicationHealth(item) === 'verified').length },
                { label: 'Need attention', value: publications.filter((item) => publicationHealth(item) === 'needs_attention').length },
                { label: 'Awaiting first publish', value: publications.filter((item) => !item.external_id).length },
              ]} />
              {publications.map((publication) => (
                <Paper key={publication.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ md: 'center' }}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle2">{publication.connection?.display_name || labelize(publication.provider)}</Typography>
                        <StatusChip status={publicationHealth(publication)} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">Desired status: {labelize(publication.status)} · Remote ID: {publication.external_id || 'not linked'}</Typography>
                      <Typography variant="caption" color={publication.last_error ? 'error' : 'text.secondary'}>{publication.last_error || (publication.last_verified_at ? `Verified ${formatDate(publication.last_verified_at)}` : 'Not verified yet')}</Typography>
                    </Box>
                    <Button variant="outlined" onClick={() => setManagedPublication(publication)}>Review and plan</Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </SectionCard>
      ) : null}

      {tab === 5 ? (
        <ResourceActivityPanel
          resources={isNew ? [] : [
            { type: 'listing', id },
            ...publications.map((publication) => ({ type: 'publication', id: publication.id })),
          ]}
          title="Listing activity"
          description="Publication workflows, retries, and provider verification across every configured destination."
        />
      ) : null}
      </Box>
      {tabs.map((label, index) => index === tab ? null : (
        <Box key={label} role="tabpanel" id={`listing-editor-panel-${index}`} aria-labelledby={`listing-editor-tab-${index}`} hidden />
      ))}

      <CreatePublicationDialog open={createPublicationOpen} listingID={listing?.id} onClose={() => setCreatePublicationOpen(false)} onCreated={loadListing} />
      <ManagePublicationDialog open={Boolean(managedPublication)} publication={managedPublication} onClose={() => setManagedPublication(null)} onChanged={loadListing} />
      <CanonicalPricingPolicyDialog open={pricingPolicyOpen} policy={editingPricingPolicy} onClose={() => setPricingPolicyOpen(false)} onSaved={pricingPolicySaved} />
    </EditorPageShell>
  );
};

export default CanonicalListingEditor;
